import Loader from "../../components/Loader";
import { useCanOpenRoom } from "../../domains/room/hooks/useCanOpenRoom";
import { useCanListRoomPodcasts } from "../../domains/podcast/hooks/useCanListRoomPodcasts";
import { RoomJoinOffer } from "../../components/room/DiscoverableRoomBlock";
import { useSharedContentBack } from "../../domains/room/hooks/useSharedContentBack";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import ContentHeader from "../../components/app/headers/ContentHeader";
import ShareV2Icon from "../../assets/icons/share-v2.svg?react";
import { NavLang } from "../../components/app/headers/layout";
import PodcastEpisodeCard from "../../components/cards/PodcastEpisodeCard";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { RoomListWrapper } from "../../components/room/layout";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useHeroImageIsDark } from "../../domains/app/hooks/useHeroImageIsDark";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useGetPodcastsListQuery } from "../../domains/podcast/hooks/useGetPodcastsListQuery";
import { useSharePodcast } from "../../domains/podcast/hooks/useSharePodcast";
import { getPodcastBackground } from "../../domains/room/utils/getPodcastBackground";
import { useApiQuery } from "../../services/api";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventViewItemListPodcastEpisodes } from "../../tools/analytics/domains/podcast.analytics";

export default function RoomPodcastPage({
  roomId,
  podcastId,
}: {
  roomId: string;
  podcastId: string;
}) {
  const { t } = useTranslation();
  const { contentLang } = useActiveLang();
  // Same shape as PlaylistPage: the cover art fills a band BEHIND a transparent
  // header, so the header only earns its frosted background and its divider
  // once the reader has scrolled onto the white body.
  const [isScrolled, setScrolled] = useState(false);
  const { discoveryLink } = usePageLinks(roomId);
  const canOpenRoom = useCanOpenRoom(roomId);
  const handleBack = useSharedContentBack(roomId);
  const { triggerShare } = useSharePodcast();

  const podcastQuery = useApiQuery(
    "/webapp/public/podcasts/:id/detail",
    podcastId === "" ? false : { param: { id: podcastId }, query: { roomId } }
  );

  const podcast = podcastQuery.state === "success" ? podcastQuery.body : null;

  // A Podcast is a language-scoped record — the FR show is a different row than
  // the EN one, with nothing linking them. The only key the data offers is the
  // show identity itself: one podcast per (room, owner, language). So when the
  // reader switches the content language, look the counterpart up in the room's
  // podcasts for that language and swap the URL to it, staying on this page.
  const canListRoomPodcasts = useCanListRoomPodcasts(roomId);
  const roomPodcastsQuery = useGetPodcastsListQuery(
    canListRoomPodcasts ? roomId : null
  );
  // A reader who opened a shared link and is not in the room cannot list its
  // podcasts, so no counterpart can be found for them: they get the podcast
  // they were sent, in its own language, rather than a dead end.
  const languageMismatch =
    podcast !== null &&
    podcast.language !== contentLang &&
    !roomPodcastsQuery.isListUnavailable;
  const ownerId =
    podcast === null || podcast.owner === null ? null : podcast.owner.id;
  const counterparts = languageMismatch
    ? roomPodcastsQuery.podcasts.filter(
        (p) => (p.owner === null ? null : p.owner.id) === ownerId
      )
    : [];
  const counterpart =
    counterparts.length === 1 ? counterparts.at(0) : undefined;

  const episodes =
    podcast === null
      ? []
      : podcast.episodes
          .toSorted((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .filter((episode) => episode.meta.status === "published");

  const episodeIds = episodes.map((episode) => episode.id).join(",");

  const firstEpisode = episodes.at(0);
  const coverUrl =
    firstEpisode === undefined || firstEpisode.thumbnail === null
      ? getPodcastBackground(0)
      : firstEpisode.thumbnail.url;
  const ownerName =
    podcast === null || podcast.owner === null ? null : podcast.owner.name;
  const coverIsDark = useHeroImageIsDark(coverUrl);
  const headerContentColor =
    !isScrolled && coverIsDark === true ? "light" : "dark";

  useEffectOnce(() => {
    if (episodes.length > 0 && podcast !== null && !languageMismatch) {
      gaEventViewItemListPodcastEpisodes({
        podcast,
        episodes,
        listName: AnalyticsItemList.ROOM_PODCAST,
      });
    }
  }, [episodeIds]);

  // The room only for a reader who can open it: a shared link brings readers
  // the room refuses, and its discovery page would bounce them on.
  if (podcastQuery.state === "error") {
    if (canOpenRoom === "pending") return <Loader expanded />;
    return <Redirect to={canOpenRoom === "open" ? discoveryLink() : "/"} />;
  }

  if (counterpart !== undefined) {
    return <Redirect to={`/room/podcast/${roomId}/${counterpart.id}`} />;
  }

  return (
    <ScrollableLayout
      headerBackground={isScrolled ? "rgba(255, 255, 255, 0.9)" : "transparent"}
      headerStyle={{
        backdropFilter: isScrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: isScrolled ? "blur(12px)" : "none",
        transition: "background 0.2s ease, backdrop-filter 0.2s ease",
      }}
      onScroll={(e) => {
        setScrolled(e.currentTarget.scrollTop > 20);
      }}
      style={{ paddingTop: "0px" }}
      header={
        <ContentHeader
          onBack={handleBack}
          transparent
          contentColor={headerContentColor}
          // ContentHeader is md:hidden by default; force it visible on desktop
          // too, like the other room resource pages: a shared link has no
          // sidebar, so this bar holds the only Back, Share and language.
          className={isScrolled ? "md:block" : "md:block border-b-0"}
          actions={
            <>
              {podcast === null ? null : (
                <ShareV2Icon
                  className="cursor-pointer"
                  onClick={() => {
                    void triggerShare(podcast, roomId);
                  }}
                />
              )}
              <NavLang />
            </>
          }
        />
      }
    >
      <div className="min-h-full bg-white pb-12 font-dm-sans">
        {/* Blurred cover behind the transparent header. Its height must match
            the header (px-3.5 py-3 → 48px) plus the safe-area inset, or the
            band leaks below it. */}
        <div
          className="relative w-full overflow-hidden"
          style={{ height: "calc(48px + var(--safe-area-top))" }}
        >
          <img
            src={coverUrl}
            alt=""
            className="absolute size-full object-cover"
          />
          <div className="absolute inset-0 backdrop-blur-[10px]" />
        </div>

        {podcast === null ||
        (languageMismatch && roomPodcastsQuery.isLoading) ? (
          <div className="flex flex-col gap-4 pt-4">
            <div className="mx-5 h-7 animate-pulse rounded bg-ds-slate-100" />
            <RoomListWrapper className="px-5">
              {Array.from({ length: 3 }).map((_, index) => (
                <SkeletonPublicationCard
                  showImage
                  key={index}
                  showDescription={false}
                />
              ))}
            </RoomListWrapper>
          </div>
        ) : languageMismatch ? (
          <p className="m-0 px-5 pt-4 text-footnote text-ds-slate-600">
            {t("This podcast is not available in this language")}
          </p>
        ) : (
          <>
            <RoomJoinOffer roomId={roomId} />
            <div className="flex flex-col gap-2 px-5 pt-5">
              <h1 className="m-0 text-heading-lg font-bold text-ds-black">
                {podcast.title}
              </h1>
              {podcast.description === null ||
              podcast.description === "" ? null : (
                <p className="m-0 text-ui-body text-ds-slate-600">
                  {podcast.description}
                </p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <img
                  src={coverUrl}
                  alt=""
                  className="size-7.5 rounded-full object-cover"
                />
                <div className="flex flex-col">
                  {ownerName === null ? null : (
                    <span className="truncate text-caption font-semibold text-ds-black">
                      {ownerName}
                    </span>
                  )}
                  <span className="truncate text-caption font-normal text-ds-slate-600">
                    {t("{{count}} episodes", { count: episodes.length })}
                  </span>
                </div>
              </div>
            </div>

            {episodes.length === 0 ? (
              <p className="m-0 px-5 pt-6 text-footnote text-ds-slate-600">
                {t("This podcast has no episodes")}
              </p>
            ) : (
              <RoomListWrapper className="px-5 pt-2">
                {episodes.map((episode, index) => (
                  <PodcastEpisodeCard
                    key={episode.id}
                    roomId={roomId}
                    podcast={podcast}
                    episode={episode}
                    listIndex={index}
                    analyticsListName={AnalyticsItemList.ROOM_PODCAST}
                  />
                ))}
              </RoomListWrapper>
            )}
          </>
        )}
      </div>
    </ScrollableLayout>
  );
}
